import { Link, useSearchParams } from 'react-router'
import { oauthBrowserErrorSchema } from '@lib/enums/oauth-browser-error.enum'
import type { OauthBrowserError } from '@lib/enums/oauth-browser-error.enum'
import { Button } from '@/components/ui/button'

/**
 * One sentence per code the API is allowed to put on this page.
 *
 * A `Record` so adding a code without a sentence fails here, at compile time,
 * instead of falling through to the generic line only in production.
 */
const OAUTH_BROWSER_ERROR_MESSAGES: Record<OauthBrowserError, string> = {
  invalid_request: 'That connection link is not valid.',
  invalid_client: 'Smithwell could not verify that app.',
  invalid_scope: 'That app asked for something Smithwell does not allow.',
  server_error: 'Something went wrong before the connection screen.',
}

/**
 * A connection that failed before the connect screen.
 *
 * Public, and it stays here. The sentences are Smithwell's. Nothing else in
 * the query string is shown, and the browser is not sent back to the app.
 */
export function OauthErrorPage() {
  const [params] = useSearchParams()
  const message = oauthErrorMessage(params.get('error'))

  return (
    <div className="flex min-h-svh items-center justify-center bg-app p-4">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-default bg-surface p-4">
        <h1 className="text-xl font-semibold">Could not connect</h1>
        <p className="text-sm text-secondary-text">{message}</p>
        <Button variant="outline" asChild>
          <Link to="/login">Back to sign in</Link>
        </Button>
      </div>
    </div>
  )
}

function oauthErrorMessage(code: string | null): string {
  const parsed = oauthBrowserErrorSchema.safeParse(code)
  if (!parsed.success) return 'Smithwell could not open that connection.'
  return OAUTH_BROWSER_ERROR_MESSAGES[parsed.data]
}
